import type { Story } from '@/types'
import type {
  RecordStrategy,
  RecordStrategyArgs,
  SearchableData,
} from '../types'
import { useStories } from '@/store/modules/App/stories'
import { getStatus } from '@/utils/storyStatus'
import { useSpace } from '@/store/modules/App/space'
import { orderBy } from 'lodash'
import { DEFAULT_PAGINATION } from '@/constants/pagination'

export class StoriesRecord implements RecordStrategy {
  async set(args: RecordStrategyArgs = {}): Promise<void> {
    const { spaceId } = args
    const { isBlockedOrUnpaid } = useSpace()

    if (!spaceId || isBlockedOrUnpaid) {
      return
    }
    const stories = await this.loadStories(spaceId)

    window?.CommandBar?.addRecords(
      'stories',
      this.createSearchableRecord(stories, spaceId),
      {
        onInputChange: (searchTerm: string) =>
          this.onSearchStories(spaceId, searchTerm, stories),
        searchTabEnabled: true,
      },
    )

    this.addLastUpdatedStoriesCommands(stories, spaceId)
  }

  async onSearchStories(
    spaceId: string,
    searchTerm: string,
    stories: Story[],
  ): Promise<SearchableData[]> {
    let results = stories?.filter((story) =>
      story.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    if (!results?.length) {
      results = await this.loadStories(spaceId, searchTerm)
    }

    return this.createSearchableRecord(results, spaceId)
  }

  async loadStories(
    spaceId: string,
    searchTerm: string = '',
  ): Promise<Story[]> {
    if (!spaceId) {
      return []
    }
    try {
      const { data } = await useStories().loadStories({
        spaceId: spaceId,
        apiParameters: {
          page: DEFAULT_PAGINATION.currentPage,
          per_page: DEFAULT_PAGINATION.perPage,
          search: searchTerm,
          sort_by: 'updated_at:desc',
        },
        noCommit: true,
      })

      return data.stories
    } catch (error) {
      console.error('Error fetching stories', error)
      return []
    }
  }

  createSearchableRecord(records: Story[], spaceId: string): SearchableData[] {
    return records
      .filter((item) => !item.can_not_view)
      .map((item: Story) => ({
        spaceId,
        id: item.id,
        title: item.name,
        label: item.name,
        description: this.computedSlug(item),
        url: this.getStoryURL(item, spaceId),
        icon: this.mapIconBasedOnStatus(item),
      }))
  }

  computedSlug(story: Story): string {
    return !story.is_folder ? story.full_slug : story.slug
  }

  getStoryURL(story: Story, spaceId: string): string {
    if (story.is_folder) {
      return `/#/me/spaces/${spaceId}/stories/0/0/index/${story.id}`
    }
    return `/#/me/spaces/${spaceId}/stories/0/0/${story.id}`
  }

  mapIconBasedOnStatus(story: Story): string {
    const { getLang, getSpaceOptions } = useSpace()
    const status = getStatus(story, getLang, getSpaceOptions)

    const icons: Record<string, string> = {
      folder: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 25" fill="none"><path fill="currentColor" d="M19 17.167c0 .353-.148.692-.41.942-.263.25-.619.391-.99.391H6.4c-.371 0-.727-.14-.99-.39S5 17.52 5 17.166V7.833c0-.353.147-.692.41-.942s.619-.391.99-.391h2.98a1 1 0 0 1 .819.427L11 8.073a1 1 0 0 0 .82.427h5.78c.371 0 .727.14.99.39.262.25.41.59.41.943v7.334Z"/></svg>`,
      published:
        'https://a.storyblok.com/f/136045/24x25/45dc8dc458/story-status-published.svg',
      draft:
        'https://a.storyblok.com/f/136045/24x25/93b423af5e/story-draft-status.svg',
      scheduled:
        'https://a.storyblok.com/f/136045/24x25/0662a6e939/status-scheduled.svg',
      'published-has-changes':
        'https://a.storyblok.com/f/136045/24x24/7a512f5d7a/story-status-has-changes.svg',
    }

    if (story.is_folder) {
      return icons.folder
    }
    return icons[status.class] || icons.draft
  }

  remove(): void {
    window?.CommandBar?.addRecords('stories', [], {
      onInputChange: async (): Promise<SearchableData[]> => [],
      searchTabEnabled: false,
    })
  }

  addLastUpdatedStoriesCommands(stories: Story[], spaceId: string): void {
    const lastUpdatedStories = orderBy(stories, 'updated_at', 'desc')
    const fiveLastUpdatedStories = lastUpdatedStories?.slice(0, 5)

    const categoryId = 16684
    this.clearLastUpdatedStoryCommands(categoryId)
    fiveLastUpdatedStories.forEach((story) =>
      window?.CommandBar?.addCommand({
        text: story.name,
        icon: this.mapIconBasedOnStatus(story),
        explanation: this.computedSlug(story),
        name: story.id.toString(),
        template: {
          type: 'link',
          value: `/#/me/spaces/${spaceId}/stories/0/0/${story.id}`,
          operation: 'self',
        },
        category: 16684,
        availability_rules: [
          {
            type: 'url',
            operator: 'includes',
            value: `/#/me/spaces/${spaceId}`,
          },
        ],
      }),
    )
  }

  clearLastUpdatedStoryCommands(categoryId: number): void {
    const commands =
      window?.CommandBar?.getCommands(
        (entry) => entry.category === categoryId,
      ) ?? []
    commands.forEach(({ command }) => {
      if (command?.toString()) {
        window?.CommandBar?.removeCommand(command?.toString())
      }
    })
  }
}
